'use client';

import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '~/components/ui/select';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { SimpleEquipment, SimplePractitioner, SimpleRoom, Step, useCreateOrModifyAppointmentStore } from '~/stores/create-appointment-store';
import { useShallow } from 'zustand/react/shallow';
import { Equipment, Practitioner, Room, useAppointmentsCalendarStore } from '~/stores/appointments-calendar-store';
import { AutoComplete } from '~/components/ui/autocomplete';
import { useUserStore } from '~/stores/user-store';
import { trpc } from '~/api/trpc';
import { AlertTriangle, Check, Pencil, X } from 'lucide-react';
import { Input } from '~/components/ui/input';
import { Tooltip, TooltipContent, TooltipTrigger } from '~/components/ui/tooltip';
import { PractitionerNameWithCategory } from '~/components/practitioners/PractitionerNameWithCategory';
import { RoomName } from '~/components/rooms/RoomName';

// An appointment usually takes place in a single room, so flag any step whose assigned
// room(s) deviate from the room combination shared by the majority of the other steps.
const getStepIdsWithRoomMismatch = (steps: Step[]): Set<string> => {
    const roomSignature = (step: Step): string | null =>
        step.rooms.length > 0
            ? step.rooms.map((room) => room.id).sort((a, b) => a - b).join(',')
            : null;

    const signatureCounts = new Map<string, number>();
    steps.forEach((step) => {
        const signature = roomSignature(step);
        if (signature !== null) {
            signatureCounts.set(signature, (signatureCounts.get(signature) || 0) + 1);
        }
    });

    // Nothing to compare against (fewer than two distinct room combinations in use)
    if (signatureCounts.size <= 1) {
        return new Set<string>();
    }

    let majoritySignature: string | null = null;
    let majorityCount = 0;
    for (const step of steps) {
        const signature = roomSignature(step);
        if (signature === null) continue;
        const count = signatureCounts.get(signature)!;
        if (count > majorityCount) {
            majorityCount = count;
            majoritySignature = signature;
        }
    }

    const mismatchedStepIds = new Set<string>();
    steps.forEach((step) => {
        const signature = roomSignature(step);
        if (signature !== null && signature !== majoritySignature) {
            mismatchedStepIds.add(step.id);
        }
    });

    return mismatchedStepIds;
};

const RoomMismatchWarning = () => (
    <Tooltip>
        <TooltipTrigger asChild>
            <span className='inline-flex'>
                <AlertTriangle className='w-4 h-4 text-yellow-500' aria-label='Abweichender Raum' />
            </span>
        </TooltipTrigger>
        <TooltipContent>
            Dieser Schritt hat einen anderen Raum zugewiesen als die übrigen Schritte des Termins.
        </TooltipContent>
    </Tooltip>
);

type SortableStepResourceSelectorProps = {
    stepId: string;
}
const SortableStepResourceSelector = ({ stepId }: SortableStepResourceSelectorProps) => {
    const { steps, setSteps, refreshSelectableResources } = useCreateOrModifyAppointmentStore(
        useShallow(state => ({
            steps: state.steps,
            setSteps: state.setSteps,
            refreshSelectableResources: state.refreshSelectableResources,
        }))
    );

    const step = useMemo(() => {
        return steps.find((s) => s.id === stepId);
    }, [stepId, steps]);

    const hasRoomMismatch = useMemo(() => getStepIdsWithRoomMismatch(steps).has(stepId), [steps, stepId]);

    const { selectedStore } = useAppointmentsCalendarStore(
        useShallow((state) => ({
            selectedStore: state.selectedStore,
        }))
    );

    const { timezone } = useUserStore(
        useShallow(state => ({
            timezone: state.user?.timezone || 'Europe/Berlin',
        }))
    )

    const trpcUtils = trpc.useUtils();

    const handleAddResource = (
        type: 'practitioners' | 'rooms' | 'equipment',
        resource: SimplePractitioner | SimpleRoom | SimpleEquipment
    ) => {
        if (!step) return;

        setSteps(steps.map(s => {
            if (s.id === stepId) {
                return {
                    ...s,
                    [type]: [...s[type], resource]
                };
            }
            return s;
        }));

        if (selectedStore?.id) {
            refreshSelectableResources(selectedStore.id, timezone, trpcUtils);
        }
    };

    const handleRemoveResource = (
        type: 'practitioners' | 'rooms' | 'equipment',
        resourceId: number
    ) => {
        if (!step) return;

        setSteps(steps.map(s => {
            if (s.id === stepId) {
                return {
                    ...s,
                    [type]: s[type].filter((r) => r.id !== resourceId)
                };
            }
            return s;
        }));

        if (selectedStore?.id) {
            refreshSelectableResources(selectedStore.id, timezone, trpcUtils);
        }
    };

    const renderResourceAutocomplete = useCallback((
        type: 'practitioners' | 'rooms' | 'equipment',
        placeholder: string
    ) => {
        const [searchValue, setSearchValue] = useState('');

        if (!step) return null;
        let _resources: typeof step.selectablePractitioners | typeof step.selectableRooms | typeof step.selectableEquipment = [];
        let acceptableConstraints: any[] | null = null;

        switch (type) {
            case 'practitioners':
                _resources = step.selectablePractitioners;
                acceptableConstraints = step.acceptablePractitioners;
                break;
            case 'rooms':
                _resources = step.selectableRooms;
                acceptableConstraints = step.acceptableRooms;
                break;
            case 'equipment':
                _resources = step.selectableEquipment;
                acceptableConstraints = step.acceptableEquipments;
                break;
            default:
                break;
        }
        const resources = _resources;
        const initiallySelectedResources: SimplePractitioner[] | SimpleRoom[] | SimpleEquipment[] =
            type === 'practitioners' ? step.initialPractitioners :
                type === 'rooms' ? step.initialRooms :
                    type === 'equipment' ? step.initialEquipment
                        : [];

        const selectedResources: SimplePractitioner[] | SimpleRoom[] | SimpleEquipment[] =
            type === 'practitioners' ? step.practitioners :
                type === 'rooms' ? step.rooms :
                    type === 'equipment' ? step.equipment
                        : [];

        const isResourceRequired = acceptableConstraints && acceptableConstraints.length > 0;

        const isSatisfied =
            type === 'practitioners' ? !step.acceptablePractitioners || step.acceptablePractitioners.length === 0 || step.acceptablePractitioners.some((constraint) => step.practitioners.some((prac) => prac.categoryId === constraint.categoryId)) :
                type === 'rooms' ? !step.acceptableRooms || step.acceptableRooms.length === 0 || step.acceptableRooms.some((constraint) => step.rooms.some((room) => room.categoryId === constraint.categoryId)) :
                    type === 'equipment' ? !step.acceptableEquipments || step.acceptableEquipments.length === 0 || step.acceptableEquipments.some((constraint) => step.equipment.some((equip) => equip.categoryId === constraint.categoryId))
                        : false;

        // Filter out already selected resources
        const availableOptions: typeof resources = resources.filter(resource =>
            !selectedResources.some((selected) => selected.id === resource.id)
        );

        // Filter options based on search value
        const filteredOptions: typeof resources = searchValue.trim()
            ? availableOptions.filter(resource => {
                const label = resource.name;
                return label.toLowerCase().includes(searchValue.toLowerCase());
            })
            : availableOptions;

        const options = filteredOptions.sort((a, b) => {
            if ('canPerform' in a && 'canPerform' in b) {
                if (a.canPerform as Boolean === true && b.canPerform as Boolean === false) {
                    return -1;
                }
                if (a.canPerform as Boolean === false && b.canPerform as Boolean === true) {
                    return 1;
                }
            }
            return a.isAvailable === true ? -1 : 1;
        }).map(resource => {
            const label = resource.name;
            const category = resource.categoryName;

            const selectableResource = resources.find((selectable) => selectable.id === resource.id);
            const isAvailable = selectableResource?.isAvailable === undefined ? true : selectableResource.isAvailable;
            const isShiftAvailable = selectableResource && 'isShiftAvailable' in selectableResource ? selectableResource.isShiftAvailable as Boolean : true;
            const isBlocked = selectableResource && 'isBlocked' in selectableResource ? selectableResource.isBlocked as Boolean : false;
            const numberOfExistingBookings = selectableResource?.numberOfExistingBookings ?? 0;
            const resourceAlreadyAssignedToThisStep = initiallySelectedResources.some((r) => r.id === resource.id);
            const isBusy = isBlocked === true || !isShiftAvailable ||
                (numberOfExistingBookings === 0 && !isAvailable) ||
                (numberOfExistingBookings === 1 && !isAvailable && !resourceAlreadyAssignedToThisStep) ||
                (numberOfExistingBookings > 1 && !isAvailable)
            const canPerform = 'canPerform' in resource ? resource.canPerform as Boolean : true;

            const isUnnecessaryResource = !isResourceRequired;

            let finalWarningText: string | null = null;

            if (isUnnecessaryResource) {
                finalWarningText = 'unnötige Ressource'; 
            } else if (isBusy || !canPerform) {
                finalWarningText = !canPerform ? 'nicht zugelassen' : 'nicht verfügbar';
            }

            return {
                value: String(resource.id),
                label,
                renderItem: (
                    <div className={`flex min-w-0 flex-col ${!canPerform ? 'opacity-60' : ''}`}>
                        {type === 'rooms' ? (
                            <RoomName name={label} categoryName={category} />
                        ) : (
                            <PractitionerNameWithCategory
                                name={label}
                                categoryName={category}
                            />
                        )}
                        {finalWarningText && <span className='text-xs text-red-400'>({finalWarningText})</span>}
                    </div>
                )
            };
        });

        return (
            <div>
                <AutoComplete
                    selectedValue=""
                    onSelectedValueChange={(value) => {
                        if (value) {
                            const resource = resources.find(r => String(r.id) === value);
                            if (resource) {
                                handleAddResource(type, resource);
                                setSearchValue(''); // Clear search after selection
                            }
                        }
                    }}
                    searchValue={searchValue}
                    onSearchValueChange={(value) => setSearchValue(value)}
                    items={options}
                    placeholder={placeholder}
                    resetSearchOnSelectItem={true}
                    className={isSatisfied ? 'border-green-600' : 'border-red-600'}
                    popoverClassName={type === 'rooms' ? 'w-auto min-w-[var(--radix-popover-trigger-width)] max-w-[min(24rem,90vw)]' : undefined}
                />
                {selectedResources.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                        {selectedResources.map((resource) => {
                            const label = resource.name;

                            const selectableResource = resources.find((selectable) => selectable.id === resource.id);
                            const isAvailable = selectableResource?.isAvailable === undefined ? true : selectableResource.isAvailable;
                            const isShiftAvailable = selectableResource && 'isShiftAvailable' in selectableResource ? selectableResource.isShiftAvailable as Boolean : true;
                            const isBlocked = selectableResource && 'isBlocked' in selectableResource ? selectableResource.isBlocked as Boolean : false;
                            const numberOfExistingBookings = selectableResource?.numberOfExistingBookings ?? 0;
                            const resourceAlreadyAssignedToThisStep = initiallySelectedResources.some((r) => r.id === resource.id);
                            const isBusy = isBlocked === true || !isShiftAvailable ||
                                (numberOfExistingBookings === 0 && !isAvailable) ||
                                (numberOfExistingBookings === 1 && !isAvailable && !resourceAlreadyAssignedToThisStep) ||
                                (numberOfExistingBookings > 1 && !isAvailable)
                            const canPerform = 'canPerform' in resource ? resource.canPerform as Boolean : true;

                            const isUnnecessaryResource = !isResourceRequired;

                            let finalColorClass = 'bg-green-600';
                            let finalWarningText: string | null = null;
                            let warningTextColorClass = 'text-gray-200';

                            if (isUnnecessaryResource) {
                                finalColorClass = 'bg-red-600'; 
                                finalWarningText = 'unnötige Ressource'; 
                                warningTextColorClass = 'text-white';
                            } else if (isBusy || !canPerform) {
                                finalColorClass = 'bg-amber-600';
                                finalWarningText = !canPerform ? 'nicht zugelassen' : 'nicht verfügbar';
                            }

                            return (
                                <div
                                    key={resource.id}
                                    className={`flex items-center gap-1 text-white text-xs px-2 py-1 rounded-md ${finalColorClass}`}
                                >
                                    <div className='flex flex-col gap-0'>
                                        <span>{label}</span>
                                        {type === 'practitioners' && 'categoryName' in resource && resource.categoryName && (
                                            <span className='text-white/80 text-[9px] leading-none'>{resource.categoryName}</span>
                                        )}
                                        {finalWarningText && <span className='text-xs text-white/80 text-[9px]'>({finalWarningText})</span>}
                                    </div>
                                    <button
                                        onClick={() => handleRemoveResource(type, resource.id)}
                                        className="text-muted-foreground hover:text-destructive"
                                        aria-label={`Remove ${label}`}
                                    >
                                        <X className='w-3 h-3' />
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                )}
                {isSatisfied === false && (
                    <div className="text-red-400 text-xs mt-1">
                        <span className='flex'>Notwendig</span>
                    </div>
                )}
            </div>
        );
    }, [steps, stepId, handleAddResource, handleRemoveResource]);

    return (
        <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
                <h3 className="font-medium text-xs">Behandler</h3>
                {renderResourceAutocomplete('practitioners', 'Behandler auswählen...')}
            </div>

            <div className="space-y-2">
                <h3 className="font-medium text-xs flex items-center gap-1.5">
                    Räume
                    {hasRoomMismatch && <RoomMismatchWarning />}
                </h3>
                {renderResourceAutocomplete('rooms', 'Raum auswählen...')}
            </div>

            <div className="space-y-2">
                <h3 className="font-medium text-xs">Geräte</h3>
                {renderResourceAutocomplete('equipment', 'Gerät auswählen...')}
            </div>
        </div>
    );
}

type SortableStepDeleteButtonProps = {
    stepId: string;
}
const SortableStepDeleteButton = ({ stepId }: SortableStepDeleteButtonProps) => {
    const { steps, setSteps, selectedBookableItems, removeBookableItem, refreshSelectableResources } = useCreateOrModifyAppointmentStore(
        useShallow(state => ({
            steps: state.steps,
            setSteps: state.setSteps,
            selectedBookableItems: state.selectedBookableItems,
            removeBookableItem: state.removeBookableItem,
            refreshSelectableResources: state.refreshSelectableResources,
        }))
    );

    const step = useMemo(() => {
        return steps.find((s) => s.id === stepId);
    }, [steps, stepId]);

    const { selectedStore } = useAppointmentsCalendarStore(
        useShallow((state) => ({
            selectedStore: state.selectedStore,
        }))
    );

    const { timezone } = useUserStore(
        useShallow(state => ({
            timezone: state.user?.timezone || 'Europe/Berlin',
        }))
    )

    const trpcUtils = trpc.useUtils();

    const handleDelete = () => {
        if (step && step.bookableItemId) {
            // Check if this is the last step for this bookable item
            const stepsWithSameBookableItem = steps.filter((s) => s.bookableItemId === step.bookableItemId);
            if (stepsWithSameBookableItem.length === 1) {
                // Last step with this bookable item -- remove the bookable item (which also removes associated steps)
                removeBookableItem(step.bookableItemId);
            } else {
                // Just remove this step, keep the bookable item
                setSteps(steps.filter(s => s.id !== stepId));
            }
        } else {
            // Custom step without bookable item - just remove
            setSteps(steps.filter(s => s.id !== stepId));
        }

        if (selectedStore?.id) {
            refreshSelectableResources(selectedStore.id, timezone, trpcUtils);
        }
    }

    return (
        <button
            onClick={handleDelete}
            className="text-gray-500 hover:text-red-500 z-10 ml-auto"
            aria-label="Delete step"
        >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
        </button>
    );
}

// --- SortableStep Props ---
type SortableStepProps = {
    id: string;
    name: string;
    shortDescription: string;
    minuteStart: number;
    minuteEnd: number;
    treatmentColor: string;
    onDurationChange?: (id: string, newDuration: number) => void;
    onRename?: (id: string, newName: string) => void;
};

export const SortableStep = ({
    id,
    name,
    shortDescription,
    minuteStart,
    minuteEnd,
    treatmentColor,
    onDurationChange,
    onRename,
}: SortableStepProps) => {
    const { steps } = useCreateOrModifyAppointmentStore(
        useShallow(state => ({
            steps: state.steps,
        }))
    );

    const hasRoomMismatch = useMemo(() => getStepIdsWithRoomMismatch(steps).has(id), [steps, id]);

    // --- Use useSortable ---
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition, // Get transition for smoother movement
        isDragging, // Use isDragging from useSortable
    } = useSortable({
        id,
        // data: { // Data can still be passed if needed elsewhere
        //     minuteStart,
        //     minuteEnd,
        //     duration: minuteEnd - minuteStart,
        // },
    });

    // --- Rename state ---
    const [isEditing, setIsEditing] = useState(false);
    const [editName, setEditName] = useState(name);

    useEffect(() => {
        setEditName(name);
    }, [name]);

    const handleConfirmRename = () => {
        const trimmed = editName.trim();
        if (trimmed && trimmed !== name && onRename) {
            onRename(id, trimmed);
        } else {
            setEditName(name);
        }
        setIsEditing(false);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleConfirmRename();
        } else if (e.key === 'Escape') {
            setEditName(name);
            setIsEditing(false);
        }
    };

    // --- Calculate Duration ---
    const duration = minuteEnd - minuteStart;

    // --- Duration Selector Logic (remains largely the same) ---
    const [durationValue, setDurationValue] = useState(duration.toString());

    // Update internal state if duration prop changes externally
    useEffect(() => {
        setDurationValue(duration.toString());
    }, [duration]);


    const durationOptions = useMemo(() => {
        // ... (duration options generation remains the same)
        const allOptions = Array.from({ length: 24 }, (_, i) => {
            const minutes = (i + 1) * 5;
            let label = '';

            if (minutes >= 60) {
                const hours = Math.floor(minutes / 60);
                const remainingMinutes = minutes % 60;
                label = `${hours}h${remainingMinutes > 0 ? remainingMinutes + 'min' : ''}`;
            } else {
                label = `${minutes}min`;
            }

            return {
                label,
                value: minutes.toString(),
            };
        });
        return allOptions;
    }, []);

    const handleDurationChange = (value: string) => {
        // Update internal state immediately for responsiveness
        setDurationValue(value);
        if (onDurationChange) {
            const newDuration = parseInt(value, 10);
            if (!isNaN(newDuration)) {
                onDurationChange(id, newDuration);
            }
        }
    };

    // --- Styling for Sortable Item ---
    const style: React.CSSProperties = {
        transform: CSS.Translate.toString(transform), // Use Translate for sorting
        transition: transition || 'transform 0.2s ease', // Apply transition
        opacity: isDragging ? 0.7 : 1, // Dim when dragging
        borderLeft: `4px solid ${treatmentColor}`,
        zIndex: isDragging ? 10 : 1, // Ensure dragging item is on top
    };

    return (
        // Apply ref, style, and make the outer div the sortable node
        <div
            ref={setNodeRef}
            style={style}
            className='flex bg-white border rounded-md shadow-sm overflow-hidden'
        >
            {/* Drag Handle */}
            <div
                {...attributes} // Spread attributes here for the handle
                {...listeners} // Spread listeners here for the handle
                className='flex items-center justify-center px-2 flex-shrink-0 cursor-grab touch-none border-r bg-gray-50' // Added touch-none and bg
                aria-label="Drag to reorder step"
            >
                <svg width='14' height='14' viewBox='0 0 16 16' fill='currentColor' className='text-gray-400'>
                    <path d='M4 4h2v2H4V4zm6 0h2v2h-2V4zM4 9h2v2H4V9zm6 0h2v2h-2V9z' />
                </svg>
            </div>

            {/* Content Area */}
            <div className='flex-1 p-4 flex flex-col gap-4 min-w-0'>
                {/* Top row: Name, Duration, Delete */}
                <div className='flex flex-row items-start justify-between gap-4'>
                    <div className="flex-1 min-w-0">
                        <div className='flex flex-col gap-1'>
                            <h3 className='text-sm font-medium text-gray-900 break-words flex items-center gap-1.5'>
                                {isEditing ? (
                                    <div className="flex items-center gap-1">
                                        <Input
                                            value={editName}
                                            onChange={(e) => setEditName(e.target.value)}
                                            onKeyDown={handleKeyDown}
                                            className="h-6 text-sm py-0 px-1.5 w-48"
                                            autoFocus
                                        />
                                        <button
                                            type="button"
                                            onClick={handleConfirmRename}
                                            className="text-green-600 hover:text-green-700 shrink-0"
                                            aria-label="Confirm rename"
                                        >
                                            <Check className="w-4 h-4" />
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => { setEditName(name); setIsEditing(false); }}
                                            className="text-gray-400 hover:text-gray-600 shrink-0"
                                            aria-label="Cancel rename"
                                        >
                                            <X className="w-3.5 h-3.5" />
                                        </button>
                                    </div>
                                ) : (
                                    <>
                                        {name}
                                        <button
                                            type="button"
                                            onClick={() => setIsEditing(true)}
                                            className="text-gray-400 hover:text-gray-600 shrink-0"
                                            aria-label="Rename step"
                                        >
                                            <Pencil className="w-3.5 h-3.5" />
                                        </button>
                                    </>
                                )}
                                {hasRoomMismatch && <RoomMismatchWarning />}
                            </h3>
                            <h6 className='text-xs text-gray-500 break-words'>
                                {shortDescription}
                            </h6>
                            {/* Optional: Display calculated times */}
                            <p className="text-xs text-muted-foreground mt-1">
                                {minuteStart}min - {minuteEnd}min ({duration} min)
                            </p>
                        </div>
                    </div>
                    <div className='flex flex-row items-center justify-end gap-2 flex-shrink-0'>
                        {/* Duration Select */}
                        <Select value={durationValue} onValueChange={handleDurationChange}>
                            {/* Shrink trigger width if needed */}
                            <SelectTrigger className='w-[110px] text-xs h-8'>
                                <SelectValue placeholder='Duration' />
                            </SelectTrigger>
                            <SelectContent>
                                {durationOptions.map((option) => (
                                    <SelectItem key={option.value} value={option.value} className="text-xs">
                                        {option.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        {/* Delete Button */}
                        <SortableStepDeleteButton stepId={id} />
                    </div>
                </div>

                {/* Resource Selector */}
                <SortableStepResourceSelector stepId={id} />
            </div>
        </div>
    );
};